import { Children, useState, useEffect, useCallback, type ReactNode } from 'react';
import './sidebar.css';

const STORAGE_KEY = 'capture:sidebar:groups';

function readState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeState(key: string, expanded: boolean) {
  const s = readState();
  s[key] = expanded;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export interface SidebarGroupProps {
  label: string;
  defaultExpanded?: boolean;
  persistKey?: string;
  children: ReactNode;
}

export function SidebarGroup({ label, defaultExpanded = true, persistKey, children }: SidebarGroupProps) {
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!persistKey) return defaultExpanded;
    const s = readState();
    return persistKey in s ? s[persistKey]! : defaultExpanded;
  });

  useEffect(() => {
    if (persistKey) writeState(persistKey, expanded);
  }, [persistKey, expanded]);

  const toggle = useCallback(() => setExpanded(e => !e), []);

  // F-065 — every entry is now permission-gated, so a group can end up with
  // nothing in it. Decide that here rather than from a second table of
  // group→entry membership: `Children.toArray` drops `false`/`null` children,
  // so this can never drift from what is actually rendered.
  if (Children.toArray(children).length === 0) return null;

  return (
    <div className="sidebar-group">
      <button type="button" className="sidebar-group__header" onClick={toggle} aria-expanded={expanded}>
        <span className="sidebar-group__chevron" aria-hidden>{expanded ? '▾' : '▸'}</span>
        <span>{label}</span>
      </button>
      {expanded && <div className="sidebar-group__body">{children}</div>}
    </div>
  );
}
